import { token } from '@atlaskit/tokens';
import usePanel from '@databases/panel/usePanel';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { selectIsSoftSelectEnabled } from '@databases/redux/cellSelections/selectors';
import { isEmbed } from '@databases/utils/utils';
import React from 'react';
import { Panel, PanelResizeHandle } from 'react-resizable-panels';
import styled, { css } from 'styled-components';

import PersistedPanelGroup from './PersistedPanelGroup';
import PanelSizes from './panel/sizes';
import { storage as panelPersistedStorage } from './panel/storage';

type LayoutProps = {
  banners?: React.ReactNode;
  options?: {
    toolbarLocation: 'top' | 'views';
  };
  view: React.ReactNode;
  sidebar?: React.ReactNode | null;
  toolbar: React.ReactNode;
  outerContainerRef?: HTMLElement | null;
};

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  position: absolute;
  width: 100%;
  overflow: hidden;
  left: 0;
  top: 0;
  margin-right: ${token('space.100')};

  #app-resize-handle {
    border-left-width: 2px;
    border-left-style: solid;
    border-left-color: ${token('color.border', '#091E4224')};
  }

  #app-resize-handle[data-resize-handle-state='hover'] {
    border-left-color: ${token('color.border.focused', '#388BFF')};
  }

  #app-resize-handle[data-resize-handle-state='drag'] {
    border-left-color: ${token('color.border.selected', '#0C66E4')};
  }

  #right-side-panel {
    height: 100%;
  }
`;

const ToolbarContainer = styled.div<{ isEmbedView: boolean }>`
  padding-left: ${(prop) => (prop.isEmbedView ? token('space.0') : token('space.200'))};
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  min-height: 0;
  flex-shrink: 1;
  display: flex;
  flex-direction: row;
`;

const ViewWrapper = styled(Panel)<{ $isEmbed: boolean }>`
  padding-left: ${(prop) => (prop.$isEmbed ? token('space.0') : token('space.100'))};
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const SidebarPanel = styled(Panel)<{ $showTopBorder: boolean }>`
  ${(prop) =>
    prop.$showTopBorder &&
    css`
      border-top: 1px solid ${token('color.border', '#EBECF0')};
      margin-top: ${token('space.100')};
    `}
`;

const PanelContent = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  flex-direction: column;
  overflow: auto;
`;

const ToolbarRenderer = ({
  isEmbedView,
  toolbar,
}: {
  isEmbedView: boolean;
  toolbar: React.ReactNode;
}) => {
  return (
    <ToolbarContainer
      isEmbedView={isEmbedView}
      id="layout-toolbar-wrapper"
      data-testid={'layout-toolbar-wrapper'}
    >
      {toolbar}
    </ToolbarContainer>
  );
};

const DatabaseLayout = ({
  banners,
  view,
  sidebar,
  toolbar,
  options = {
    toolbarLocation: 'top',
  },
  outerContainerRef,
}: LayoutProps) => {
  const isEmbedView = isEmbed();
  const { getPanelComponent, getPanelKey } = usePanel();

  const panelComponent = getPanelComponent();
  const sidebarComponent = sidebar ?? panelComponent ?? null;

  const storageKey = `${isEmbedView ? 'embed' : 'confluence-content'}-${
    getPanelKey() ?? 'no-panel'
  }`;

  const dispatch = defaultStoreUtils.useDispatch();

  const isSoftSelected = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    return selectIsSoftSelectEnabled(state);
  });

  const getDefaultSize = (panelKey: 'sidebar') => {
    const layout = panelPersistedStorage.getItem(storageKey);

    return layout
      ? layout[PanelSizes[panelKey].order - 1]
      : PanelSizes[panelKey].defaultSize;
  };

  return (
    <LayoutContainer
      id="layout-container"
      data-testid={'layout-container'}
      onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const target = e.target as HTMLElement;
        if (
          'ariaLabel' in target &&
          target.ariaLabel !== 'dragHandlerIcon' &&
          target.hasAttribute('data-testid') &&
          target.getAttribute('data-testid') !== 'database-value-view-container'
        ) {
          if (!isSoftSelected) {
            dispatch(deselectAll());
          }
        }
      }}
    >
      {banners && <div data-testid="layout-banner-wrapper">{banners}</div>}
      {options.toolbarLocation === 'top' ? (
        <ToolbarRenderer isEmbedView={isEmbedView} toolbar={toolbar} />
      ) : null}
      <ContentWrapper data-testid={'layout-content-wrapper'}>
        <PersistedPanelGroup
          storageKey={storageKey}
          outerContainerRef={outerContainerRef}
        >
          <ViewWrapper
            order={PanelSizes.main.order}
            maxSize={PanelSizes.main.max}
            minSize={PanelSizes.main.min}
            id="main"
            tagName="main"
            $isEmbed={isEmbedView}
            data-testid={'layout-view--wrapper'}
          >
            {options.toolbarLocation === 'views' ? (
              <ToolbarRenderer isEmbedView={isEmbedView} toolbar={toolbar} />
            ) : null}

            {view}
          </ViewWrapper>
          <>
            {sidebarComponent ? (
              <>
                <PanelResizeHandle
                  id="app-resize-handle"
                  style={{
                    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                    marginTop:
                      options?.toolbarLocation === 'top' ? token('space.100') : 0,
                    marginLeft: 0,
                    marginRight: 0,
                    marginBottom: 0,
                  }}
                />
                <SidebarPanel
                  $showTopBorder={options?.toolbarLocation === 'top'}
                  order={PanelSizes.sidebar.order}
                  maxSize={PanelSizes.sidebar.max}
                  minSize={PanelSizes.sidebar.min}
                  defaultSize={getDefaultSize('sidebar')}
                  id="right-side-panel"
                  data-testid={'layout-right-side-panel'}
                >
                  <PanelContent>{sidebarComponent}</PanelContent>
                </SidebarPanel>
              </>
            ) : null}
          </>
        </PersistedPanelGroup>
      </ContentWrapper>
    </LayoutContainer>
  );
};

export default DatabaseLayout;
